// src/shared/components/FormSelect.jsx
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * A reusable select component with optional helper text and validation.
 * Mirrors FormInput's label/required/error/helper conventions.
 *
 * Provide `options` for the common case, or pass `children` (e.g. custom
 * <option>/<optgroup> or loading placeholders) for full control.
 */
export default function FormSelect({
  id,
  name,
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  helperText,
  required,
  disabled,
  className,
  selectClassName,
  children,
  ...rest
}) {
  const hasError = !!error;
  const selectClasses = classNames(
    'form-select',
    { 'is-invalid': hasError },
    selectClassName
  );

  return (
    <div className={classNames('mb-3', className)}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && (
            <span
              className="text-danger ms-1"
              aria-hidden="true"
              title="Required"
            >
              *
            </span>
          )}
        </label>
      )}

      <select
        id={id}
        name={name}
        className={selectClasses}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        {...rest}
      >
        {placeholder != null && <option value="">{placeholder}</option>}
        {options
          ? options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          : children}
      </select>

      {helperText && !hasError && (
        <div className="form-text">{helperText}</div>
      )}

      {hasError && (
        <div className="invalid-feedback">{error}</div>
      )}
    </div>
  );
}

FormSelect.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  label: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.node,
  })),
  placeholder: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  selectClassName: PropTypes.string,
  children: PropTypes.node,
};

FormSelect.defaultProps = {
  name: undefined,
  label: null,
  value: '',
  options: null,
  placeholder: null,
  error: null,
  helperText: null,
  required: false,
  disabled: false,
  className: '',
  selectClassName: '',
  children: null,
};
